import { codeExample, defineDemoElement, demoLayoutStyles } from './demo-shared.js';
import { css, html, LitElement } from 'lit';
import { state } from 'lit/decorators.js';
import type { DemoSection } from './demo-shared.js';

/** Removable tags with a reset, so the example never dead-ends. */
class DemoTagRemovable extends LitElement {
  static styles = [
    demoLayoutStyles,
    css`
      :host {
        display: block;
      }
    `
  ];

  @state() private tags = ['Windows', 'Doors', 'Hardware'];

  render() {
    return html`
      <div class="demo-row">
        ${this.tags.map(
          tag => html`
            <webmodule-tag
              variant="primary"
              removable
              @webmodule-remove=${() => (this.tags = this.tags.filter(existing => existing !== tag))}
            >
              ${tag}
            </webmodule-tag>
          `
        )}
        ${this.tags.length < 3
          ? html`
              <webmodule-button
                size="small"
                variant="text"
                @click=${() => (this.tags = ['Windows', 'Doors', 'Hardware'])}
              >
                Restore
              </webmodule-button>
            `
          : ''}
      </div>
    `;
  }
}

export function registerDisplayDemos() {
  defineDemoElement('demo-tag-removable', DemoTagRemovable);
}

export const tagSection: DemoSection = {
  id: 'tag',
  title: 'Tag',
  tags: ['webmodule-tag'],
  summary: 'Compact labels for statuses and categories. Removable tags emit webmodule-remove when dismissed.',
  render: () => html`
    ${codeExample({
      label: 'Variants and sizes',
      code: `
        <div class="demo-row">
          <webmodule-tag variant="primary">Primary</webmodule-tag>
          <webmodule-tag variant="success">Success</webmodule-tag>
          <webmodule-tag variant="neutral">Neutral</webmodule-tag>
          <webmodule-tag variant="warning">Warning</webmodule-tag>
          <webmodule-tag variant="danger">Danger</webmodule-tag>
        </div>
        <div class="demo-row">
          <webmodule-tag size="small">Small</webmodule-tag>
          <webmodule-tag size="medium">Medium</webmodule-tag>
          <webmodule-tag size="large">Large</webmodule-tag>
          <webmodule-tag size="medium" pill>Pill</webmodule-tag>
        </div>`
    })}
    <demo-example
      label="Removable"
      .code=${`<webmodule-tag removable @webmodule-remove=\${() => remove(tag)}>...</webmodule-tag>`}
    >
      <demo-tag-removable></demo-tag-removable>
    </demo-example>
  `
};

export const badgeSection: DemoSection = {
  id: 'badge',
  title: 'Badge',
  tags: ['webmodule-badge'],
  summary: 'Small counters and status dots that draw attention to fresh or pending items.',
  render: () => html`
    ${codeExample({
      label: 'Variants',
      code: `
        <div class="demo-row">
          <webmodule-badge variant="primary">primary</webmodule-badge>
          <webmodule-badge variant="success">success</webmodule-badge>
          <webmodule-badge variant="neutral">neutral</webmodule-badge>
          <webmodule-badge variant="warning">warning</webmodule-badge>
          <webmodule-badge variant="danger">danger</webmodule-badge>
        </div>`
    })}
    ${codeExample({
      label: 'Pill and pulse',
      code: `
        <div class="demo-row">
          <webmodule-badge variant="primary" pill>pill</webmodule-badge>
          <webmodule-badge variant="danger" pulse>pulse</webmodule-badge>
        </div>`
    })}
    ${codeExample({
      label: 'In context',
      code: `
        <div class="demo-row">
          <webmodule-button>
            Inbox
            <webmodule-badge pill>12</webmodule-badge>
          </webmodule-button>
        </div>`
    })}
  `
};

export const avatarSection: DemoSection = {
  id: 'avatar',
  title: 'Avatar',
  tags: ['webmodule-avatar'],
  summary: 'Represents a person or entity with an image, initials, or a fallback icon.',
  render: () => html`
    ${codeExample({
      label: 'Content sources',
      description: 'Falls back gracefully: image, then initials, then the icon slot / default person icon.',
      code: `
        <div class="demo-row">
          <webmodule-avatar image="assets/cat.jpg" label="Zoid the kitten"></webmodule-avatar>
          <webmodule-avatar initials="JV" label="Johan Visagie"></webmodule-avatar>
          <webmodule-avatar label="Anonymous"></webmodule-avatar>
        </div>`
    })}
    ${codeExample({
      label: 'Shapes and size',
      description: 'Shape is an attribute; overall size is the --size custom property.',
      code: `
        <div class="demo-row">
          <webmodule-avatar initials="KH" shape="circle"></webmodule-avatar>
          <webmodule-avatar initials="KH" shape="rounded"></webmodule-avatar>
          <webmodule-avatar initials="KH" shape="square"></webmodule-avatar>
          <webmodule-avatar initials="XL" style="--size: 4rem;"></webmodule-avatar>
        </div>`
    })}
  `
};

export const cardSection: DemoSection = {
  id: 'card',
  title: 'Card',
  tags: ['webmodule-card'],
  summary: 'A content container with optional image, header and footer slots.',
  render: () => html`
    ${codeExample({
      label: 'Full anatomy',
      code: `
        <webmodule-card style="max-width: 300px;">
          <img slot="image" src="assets/cat.jpg" alt="Playful kitten" />
          <strong>Zoid</strong><br />
          Playful kitten exploring a flower.<br />
          <small>Only a couple of weeks old</small>
          <div slot="footer">
            <webmodule-button variant="primary" pill>More information</webmodule-button>
          </div>
        </webmodule-card>`
    })}
    ${codeExample({
      label: 'Header slot',
      code: `
        <webmodule-card style="max-width: 300px;">
          <div slot="header" style="display: flex; align-items: center; justify-content: space-between;">
            <strong>Card title</strong>
            <webmodule-icon-button name="edit" label="Edit"></webmodule-icon-button>
          </div>
          Cards with a header slot render a divider between header and body automatically.
        </webmodule-card>`
    })}
    ${codeExample({
      label: 'Body only',
      code: `
        <webmodule-card style="max-width: 300px;">
          Just the default slot - no image, header or footer.
        </webmodule-card>`
    })}
  `
};

export const detailsSection: DemoSection = {
  id: 'details',
  title: 'Details',
  tags: ['webmodule-details'],
  summary: 'A disclosure widget: a summary line that expands to reveal more content.',
  render: () => html`
    ${codeExample({
      label: 'Basics',
      code: `
        <div class="demo-stack" style="max-width: 560px;">
          <webmodule-details summary="Want to see more? Click me">
            Details hide their content until the summary is activated, which makes them a good fit for progressive
            disclosure such as advanced settings or long explanations.
          </webmodule-details>
          <webmodule-details summary="Open by default" open>
            This one starts expanded via the open attribute.
          </webmodule-details>
          <webmodule-details summary="Disabled" disabled>You cannot expand this one.</webmodule-details>
        </div>`
    })}
    ${codeExample({
      label: 'Custom expand/collapse icons',
      code: `
        <div class="demo-stack" style="max-width: 560px;">
          <webmodule-details summary="Custom icons">
            <webmodule-icon slot="expand-icon" name="chevron-right"></webmodule-icon>
            <webmodule-icon slot="collapse-icon" name="chevron-down"></webmodule-icon>
            The expand-icon and collapse-icon slots replace the default indicator.
          </webmodule-details>
        </div>`
    })}
  `
};

export const formatSection: DemoSection = {
  id: 'format',
  title: 'Formatters',
  tags: ['webmodule-format-number', 'webmodule-format-date', 'webmodule-format-list', 'webmodule-format-relative-time'],
  summary:
    'Locale-aware text formatters built on Intl. They render plain text, so they can be dropped anywhere markup goes.',
  render: () => html`
    ${codeExample({
      label: 'Numbers',
      code: `
        <div class="demo-stack">
          <div>Percent: <webmodule-format-number type="percent" value="0.25"></webmodule-format-number></div>
          <div>Currency: <webmodule-format-number type="currency" currency="USD" value="10000"></webmodule-format-number></div>
          <div>Currency: <webmodule-format-number type="currency" currency="EUR" value="10000"></webmodule-format-number></div>
          <div>Decimal: <webmodule-format-number value="10000" minimum-fraction-digits="2"></webmodule-format-number></div>
        </div>`
    })}
    ${codeExample({
      label: 'Dates',
      description: 'With no date attribute the current date/time is formatted.',
      code: `
        <div class="demo-stack">
          <div>Default: <webmodule-format-date></webmodule-format-date></div>
          <div>Long: <webmodule-format-date month="long" day="numeric" year="numeric"></webmodule-format-date></div>
          <div>Weekday: <webmodule-format-date weekday="long"></webmodule-format-date></div>
          <div>Time: <webmodule-format-date hour="numeric" minute="numeric" hour-format="12"></webmodule-format-date></div>
        </div>`
    })}
    ${codeExample({
      label: 'Lists',
      code: `
        <div class="demo-stack">
          <div>Conjunction: <webmodule-format-list value="Johan Kyley Liselle Alex"></webmodule-format-list></div>
          <div>Disjunction: <webmodule-format-list value="Johan Kyley Liselle Alex" type="disjunction"></webmodule-format-list></div>
        </div>`
    })}
    ${codeExample({
      label: 'Relative time',
      code: `
        <div class="demo-stack">
          <div><webmodule-format-relative-time date="2025-07-02T09:17:00-04:00"></webmodule-format-relative-time></div>
          <div><webmodule-format-relative-time date="2024-07-02T09:17:00-04:00"></webmodule-format-relative-time></div>
          <div>numeric="always": <webmodule-format-relative-time numeric="always" date="2025-07-02T09:17:00-04:00"></webmodule-format-relative-time></div>
        </div>`
    })}
  `
};

export const fadeSection: DemoSection = {
  id: 'fade',
  title: 'Fade',
  tags: ['webmodule-fade'],
  summary:
    'Wraps overflowing content in a scroll region with a soft fade at the scrollable edges - a middle ground between hard truncation and full wrapping. The fade only appears on an edge while there is more content that way. Built for table cells carrying big string values.',
  render: () => html`
    ${codeExample({
      label: 'Horizontal (default)',
      description:
        'Keeps a long value on one line and scrolls sideways. The right edge fades while there is more to the right; scroll and the left edge takes over. This is the table-cell use case.',
      code: `
        <div style="max-width: 260px;">
          <webmodule-fade>
            SoftTech / Windows / Sliding Door / 2400 x 2100 / Anthracite Grey RAL 7016 / Double Glazed / Handle Left
          </webmodule-fade>
        </div>`
    })}
    ${codeExample({
      label: 'Vertical',
      description:
        'Lets content wrap and scroll within a capped height. Set the cap with the --max-block-size property.',
      code: `
        <div style="max-width: 360px;">
          <webmodule-fade orientation="vertical" style="--max-block-size: 6rem;">
            Fabrication note: seal all mitre joints before glazing. Confirm the sash clearance against the frame
            rebate and re-check the drainage slots are clear. Any deviation from the cutting list must be signed off
            by the workshop lead before the unit leaves the bench.
          </webmodule-fade>
        </div>`
    })}
    ${codeExample({
      label: 'Without shadow',
      description: 'Removes the fade cue but keeps the region scrollable.',
      code: `
        <div style="max-width: 260px;">
          <webmodule-fade without-shadow>
            SoftTech / Windows / Sliding Door / 2400 x 2100 / Anthracite Grey RAL 7016 / Double Glazed / Handle Left
          </webmodule-fade>
        </div>`
    })}
    ${codeExample({
      label: 'Without scrollbar',
      description:
        'Hides the scrollbar but keeps scrolling: mouse wheel, click-drag to pan, and keyboard. An accessibility trade-off: the region stays keyboard-scrollable but loses its visible affordance.',
      code: `
        <div style="max-width: 260px;">
          <webmodule-fade without-scrollbar>
            SoftTech / Windows / Sliding Door / 2400 x 2100 / Anthracite Grey RAL 7016 / Double Glazed / Handle Left
          </webmodule-fade>
        </div>`
    })}
    ${codeExample({
      label: 'Without scrollbar, wheel disabled',
      description:
        'Adds without-wheel so the mouse wheel scrolls the page as usual (not the cell). The content is panned by click-drag instead. Best fit for big-string table cells, where a wheel-capturing cell would make a long table feel sticky.',
      code: `
        <div style="max-width: 260px;">
          <webmodule-fade without-scrollbar without-wheel>
            SoftTech / Windows / Sliding Door / 2400 x 2100 / Anthracite Grey RAL 7016 / Double Glazed / Handle Left
          </webmodule-fade>
        </div>`
    })}
    ${codeExample({
      label: 'Fade size',
      description: 'Tune the length of the fade cue with the --fade-size property.',
      code: `
        <div style="max-width: 260px;">
          <webmodule-fade style="--fade-size: 3rem;">
            SoftTech / Windows / Sliding Door / 2400 x 2100 / Anthracite Grey RAL 7016 / Double Glazed / Handle Left
          </webmodule-fade>
        </div>`
    })}
  `
};

export const qrCodeSection: DemoSection = {
  id: 'qr-code',
  title: 'QR Code',
  tags: ['webmodule-qr-code'],
  summary: 'Renders a QR code for a value on a canvas, with configurable size, colors and error correction.',
  render: () => html`
    ${codeExample({
      label: 'Options',
      code: `
        <div class="demo-row" style="align-items: flex-start; gap: var(--webmodule-spacing-x-large);">
          <webmodule-qr-code value="https://www.softtech.com/" label="SoftTech website"></webmodule-qr-code>
          <webmodule-qr-code value="https://www.softtech.com/" size="96" radius="0.5"></webmodule-qr-code>
          <webmodule-qr-code
            value="https://www.softtech.com/"
            size="96"
            fill="var(--webmodule-color-primary-600)"
            error-correction="L"
          ></webmodule-qr-code>
        </div>`
    })}
  `
};
